import React, { useEffect, useState } from 'react';
import { AddToCartButtonProps, AddToCartButton, Cart, InventoryBatchData, InventoryInfo, LineItem } from './Types.ts';
import CommerceApiFetchWrapper from '/Foundation/Common/code/Utils/CommerceApiFetchWrapper.js';
import { setCartIdCookie } from '/Foundation/React/code/Utilities/HelperUtilities.js';
import { store } from '/Feature/Cart/code/Views/Feature/Cart/ShoppingCart/React';
import { toggleMiniCart } from '/Feature/Cart/code/Views/Feature/Cart/ShoppingCart/React/Slices/CartSlice.jsx';
import { AdobeAnalyticsUtil } from '/Foundation/Common/code/Views/Foundation/Common/AdobeAnalytics/AdobeAnalyticsUtil.js';
import { br_cartAddition } from '/Foundation/Bloomreach/code/Events/BloomreachCart.js';

const AddToCartButton = ({ addToCartButtonData, priceContainer, onComplete, disabled }: AddToCartButtonProps) => {
  const [productIds, setProductIds] = useState<string[]>([]);
  const [hasActiveRequest, setHasActiveRequest] = useState<boolean>(false);
  const [inventoryInfo, setInventoryInfo] = useState<InventoryInfo>();

  useEffect(() => {
    const getATC = async () => {
      let skus: string[] = [];

      if (addToCartButtonData.ShowDiscontinued || !window._THULEDATA.isB2CEnabled) return;

      let productIdsForSolution = addToCartButtonData.ProductIdsForSolution;
      if (productIdsForSolution) {
        productIdsForSolution.split(',').map((productId) => {
          if (productId) {
            skus.push(productId);
          }
        });
      } else {
        skus.push(addToCartButtonData.ErpOutdoor);
      }

      setProductIds(skus);

      const inventory = (await CommerceApiFetchWrapper('/cart/inventory-batch', {
        method: 'POST',
        body: JSON.stringify([{ productId: addToCartButtonData.ProductId, skuIds: skus }]),
      })) as InventoryBatchData | null;

      if (inventory?.items?.length) {
        setInventoryInfo(inventory.items[0]);
      }
    };

    getATC();
  }, [addToCartButtonData]);

  const trackAddToCart = (addtoCartButton: AddToCartButton) => {
    let product = {
      id: addtoCartButton.ProductId,
      name: addtoCartButton.AddToCartProductName,
      price: priceContainer.Price,
      colour: addtoCartButton.Colorspecification,
      original_price: priceContainer.Price,
      category: addtoCartButton.Category,
      subcategory: addtoCartButton.Subcategory,
      over_category: addtoCartButton.OverCategory,
      quantity: '1',
    };

    let analyticsData = {
      add_to_cart_variant: 'add_to_cart_findRRSforRTT',
      products: [product],
    };

    AdobeAnalyticsUtil.DataLayer.TrackEvent('cart_addition', analyticsData);
  };

  const getAvailabilityDate = (): string => {
    if (!inventoryInfo?.availabilityDate) return '';

    return new Date(inventoryInfo.availabilityDate).toLocaleDateString(window._THULEDATA.SitecoreLanguage, {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const updateCart = async () => {
    if (hasActiveRequest) return;

    setHasActiveRequest(true);

    const state = store.getState().Cart;
    let lineItems = state.lineItems?.length > 0 ? state.lineItems : window._THULEDATA.cartLineItems;

    if (lineItems?.length > 0) {
      let existingProducts: LineItem[] = [];
      let newProducts: string[] = [];
      productIds.forEach((productId) => {
        const matchingProductInCart = lineItems.find((x: any) => x.sku === productId && !x.isFreeGift);
        if (matchingProductInCart)
          existingProducts.push({
            id: matchingProductInCart.id,
            quantity: matchingProductInCart.quantity + 1,
          } as LineItem);
        else newProducts.push(productId);
      });

      if (existingProducts.length > 0) {
        let lineItems: LineItem[] = [];
        existingProducts.forEach((product: any) => {
          lineItems.push({
            id: product.id,
            quantity: product.quantity,
          } as LineItem);
        });

        const data = {
          LineItems: lineItems,
        };

        await CommerceApiFetchWrapper('/cart/update-quantity', { method: 'POST', body: JSON.stringify(data) })
          .then((data: Cart) => {
            if (newProducts.length === 0) {
              store.dispatch(toggleMiniCart(true));
            }
            br_cartAddition(data);
            window.dispatchEvent(new Event('cart-updated'));
          })
          .finally(() => {
            if (newProducts.length === 0) {
              setHasActiveRequest(false);
            }
          });
      }

      if (newProducts.length > 0) {
        const data = {
          productIds: newProducts,
          quantity: 1,
        };
        await addCartLines(data);
      }
    } else {
      const data = {
        productIds: productIds,
        quantity: 1,
      };
      await addCartLines(data);
    }

    trackAddToCart(addToCartButtonData);
  };

  const addCartLines = async (body: any) => {
    await CommerceApiFetchWrapper('/cart/add-cart-line', {
      method: 'POST',
      body: JSON.stringify(body),
    })
      .then((data: Cart) => {
        setCartIdCookie(data.id);
        store.dispatch(toggleMiniCart(true));
        onComplete();
        br_cartAddition(data);
        window.dispatchEvent(new Event('cart-updated'));
      })
      .catch((error) => {
        console.error('Error:', error);
      })
      .finally(() => {
        setHasActiveRequest(false);
      });
  };

  if (!addToCartButtonData) return null;

  return (
    <>
      {/* Sellable */}
      {addToCartButtonData.IsSellable && inventoryInfo?.isInStock && (
        <div className="cart-button-container">
          <div className="mb-2 d-flex align-items-center tpd-product-stock-status single-product">
            <span className="fs-5">{addToCartButtonData.SellableButtonSubText}</span>
          </div>
          <button
            className={`btn btn-block tag-manager-add-to-cart-button thule-btn-shop overflow-hidden w-100 position-relative${disabled ? ' disabled' : ''}`}
            disabled={disabled}
            onClick={updateCart}
          >
            {addToCartButtonData.SellableButtonText}
          </button>
        </div>
      )}
      {/* Out of stock */}
      {addToCartButtonData.IsSellable && !inventoryInfo?.isInStock && (
        <div className="cart-button-container">
          <div className="mb-2 tpd-product-stock-status single-product">
            <span className="fs-5">
              {addToCartButtonData.EstimatedDeliveryText} {getAvailabilityDate()}
            </span>
          </div>
          <button className="btn btn-block tag-manager-add-to-cart-button thule-btn-secondary disabled  w-100">
            {addToCartButtonData.OutOfStockButtonText}
          </button>
        </div>
      )}
      {/* Not buyable */}
      {!addToCartButtonData.IsSellable ||
        inventoryInfo?.notBuyable ||
        (addToCartButtonData.IsSellable && priceContainer.Price === 0 && (
          <div className="cart-button-container">
            <button className="btn tag-manager-add-to-cart-button thule-btn-secondary disabled w-100" tabIndex={-1}>
              {addToCartButtonData.NotBuyableButtonText}
            </button>
          </div>
        ))}
      {addToCartButtonData.ProductNotFound && (
        <div className="cart-button-container">
          <button className="btn tag-manager-add-to-cart-button thule-btn-secondary disabled w-100" tabIndex={-1}>
            {addToCartButtonData.ProductNotFoundButtonText}
          </button>
        </div>
      )}
      {addToCartButtonData.ShowDiscontinued && (
        <div className="btn tag-manager-add-to-cart-button thule-btn-secondary disabled w-100" tabIndex={-1}>
          {addToCartButtonData.DiscontinuedButtonText}
        </div>
      )}
    </>
  );
};

export default AddToCartButton;
